import { Button, ButtonGroup, InlineStack } from '@customerio/pluma-components/react';
import type { PushPlatform } from '@/features/push-credentials';
import { useDisablePushConfirmation } from './push-confirmations';

type EditingActionsProps = {
  isSaving: boolean;
  isUnchanged: boolean;
  canEdit: boolean;
  onSave: () => void;
  onCancel: () => void;
};

function EditingActions({ isSaving, isUnchanged, canEdit, onSave, onCancel }: EditingActionsProps) {
  return (
    <>
      <Button variant="secondary" isDisabled={isSaving} onClick={onCancel}>
        Cancel
      </Button>

      <Button variant="primary" isDisabled={!canEdit || isUnchanged} isLoading={isSaving} onClick={onSave}>
        Save changes
      </Button>
    </>
  );
}

type ConfiguredActionsProps = {
  platform: PushPlatform;
  showDisable: boolean;
  isBusy: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onStartEditing: () => void;
  onDisable: () => Promise<void>;
};

function ConfiguredActions({
  platform,
  showDisable,
  isBusy,
  canEdit,
  canDelete,
  onStartEditing,
  onDisable,
}: ConfiguredActionsProps) {
  const openDisableConfirmation = useDisablePushConfirmation({ platform, onDisable });

  return (
    <>
      {showDisable ? (
        <Button
          variant="secondary"
          isCritical={true}
          isDisabled={!canDelete || isBusy}
          onClick={openDisableConfirmation}
          data-test-push-disable-button={platform}
        >
          Disable…
        </Button>
      ) : null}

      <Button isDisabled={!canEdit || isBusy} onClick={onStartEditing}>
        Edit authorization
      </Button>
    </>
  );
}

export type PushSaveBarProps = {
  platform: PushPlatform;
  isEditing: boolean;
  isSaving: boolean;
  /** An edit of a stored credential that nothing has changed yet. */
  isUnchanged: boolean;
  isBusy: boolean;
  canEdit: boolean;
  canDelete: boolean;
  showDisable: boolean;
  onSave: () => void;
  onCancel: () => void;
  onStartEditing: () => void;
  onDisable: () => Promise<void>;
};

export function PushSaveBar({
  isEditing,
  isSaving,
  isUnchanged,
  isBusy,
  canEdit,
  canDelete,
  platform,
  showDisable,
  onSave,
  onCancel,
  onStartEditing,
  onDisable,
}: PushSaveBarProps) {
  return (
    <InlineStack alignInline="end">
      <ButtonGroup groupVariant="spaced">
        {isEditing ? (
          <EditingActions
            isSaving={isSaving}
            isUnchanged={isUnchanged}
            canEdit={canEdit}
            onSave={onSave}
            onCancel={onCancel}
          />
        ) : (
          <ConfiguredActions
            platform={platform}
            showDisable={showDisable}
            isBusy={isBusy}
            canEdit={canEdit}
            canDelete={canDelete}
            onStartEditing={onStartEditing}
            onDisable={onDisable}
          />
        )}
      </ButtonGroup>
    </InlineStack>
  );
}
